import React, { useState } from 'react'

export interface SwitchProps {
    size?: 'sm' | 'md' | 'lg'
    checked?: boolean
    defaultChecked?: boolean
    onChange?: (checked: boolean) => void
    disabled?: boolean
    label?: string
    ariaLabel?: string
    icon?: React.ReactNode
    style?: React.CSSProperties
}

const sizeConfig = {
    sm: { track: 'h-4 w-8', thumb: 'size-3', travel: 'translate-x-4', iconBox: 'size-1.5', font: 'text-xs leading-[1.34]' },
    md: { track: 'h-5 w-10', thumb: 'size-4', travel: 'translate-x-5', iconBox: 'size-2', font: 'text-sm leading-[1.43]' },
    lg: { track: 'h-6 w-12', thumb: 'size-5', travel: 'translate-x-6', iconBox: 'size-2.5', font: 'text-base leading-[1.5]' },
}

export const Switch = ({
    size = 'md',
    checked: controlledChecked,
    defaultChecked = false,
    onChange,
    disabled = false,
    label,
    ariaLabel,
    icon,
    style,
}: SwitchProps) => {
    const [internalChecked, setInternalChecked] = useState(defaultChecked)

    const checked = controlledChecked !== undefined ? controlledChecked : internalChecked
    const config = sizeConfig[size]

    const handleToggle = () => {
        if (disabled) return
        const next = !checked
        if (controlledChecked === undefined) setInternalChecked(next)
        onChange?.(next)
    }

    return (
        <div className={`inline-flex items-center gap-4 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`} style={style} onClick={handleToggle}>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                aria-label={ariaLabel ?? label}
                disabled={disabled}
                // Toggle on a direct click of the track/thumb, and stop the click from also reaching the
                // wrapper's onClick (which would toggle a second time and cancel it out). A click on the
                // label still bubbles to the wrapper. Without this, clicking the switch itself did nothing.
                onClick={(e) => {
                    e.stopPropagation()
                    handleToggle()
                }}
                onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                        e.preventDefault()
                        handleToggle()
                    }
                }}
                className={`relative shrink-0 rounded-full border-none p-0 outline-none transition-[background,box-shadow] duration-150
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background
                    ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                    ${config.track}
                    ${checked ? 'bg-accent enabled:hover:bg-accent-hover enabled:active:bg-accent-hover' : 'bg-default'}`}
            >
                <div
                    className={`absolute left-0.5 top-0.5 flex items-center justify-center rounded-full transition-transform duration-200
                        ${config.thumb}
                        ${checked ? `bg-white shadow-switch ${config.travel}` : 'bg-white shadow-field'}
                        ${disabled && checked ? 'opacity-40' : ''}`}
                >
                    {icon && <span className={`flex items-center justify-center ${config.iconBox}`}>{icon}</span>}
                </div>
            </button>
            {label && <span className={`whitespace-nowrap font-medium text-foreground ${config.font}`}>{label}</span>}
        </div>
    )
}
